import React, { useState, useEffect, useRef } from "react";

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Step,
  StepIcon,
  StepIndicator,
  StepSeparator,
  StepStatus,
  Stepper,
  VStack,
  useSteps,
  Text,
  InputRightElement,
  Heading,
  Alert,
  AlertIcon,
  CloseButton,
  IconButton,
  Flex,
} from "@chakra-ui/react";

import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faUser,
  faLock,
  faEnvelope,
  faMale,
  faEyeSlash,
  faEye,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

import LoadingSpinner from "./LoadingSpinner";
import { CloseIcon } from "@chakra-ui/icons";

const MotionBox = motion(Box);
const MotionAlert = motion(Alert);

const RegisterForm = ({
  name,
  setName,
  username,
  setUsername,
  email,
  setEmail,
  password,
  setPassword,
  phoneNumber,
  setPhoneNumber,
  confirmPassword,
  setConfirmPassword,
  isLoadingUsername,
  isLoadingEmail,
  handleUsernameValidation,
  handleEmailValidation,
  handlePhoneNumberValidation,
  handleRegister,
  handleNameValidation,
  handlePasswordValidation,
  error,
  setError,
  isCreatingAccount,
  isAccountCreated,
  handleShowLogin,
}) => {
  const steps = ["1", "2", "3", "4", "5"];
  const { activeStep, goToNext, goToPrevious } = useSteps({
    index: 0,
    count: steps.length,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState(false);

  const fadeVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const handlePrevStep = () => {
    setError("");
    goToPrevious();
  };

  const handleNextStep = async () => {
    if (activeStep === 0) {
      if (!handleNameValidation()) return;
    }

    if (activeStep === 1) {
      const isValid = await handleUsernameValidation();
      if (!isValid) return;
    }

    if (activeStep === 2) {
      const isValid =
        (await handleEmailValidation()) && handlePhoneNumberValidation();

      if (!isValid) return;
    }

    if (activeStep === 3) {
      const isValid = await handlePasswordValidation();
      if (!isValid) return;
    }

    goToNext();
  };

  useEffect(() => {
    if (isCreatingAccount) {
      goToNext();
    }
  }, [isCreatingAccount]);

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <MotionBox
            key="name"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <FormControl>
              <FormLabel fontSize="md" fontWeight="bold">
                Nombre completo
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faMale} />
                </InputLeftElement>
                <Input
                  type="text"
                  value={name}
                  fontSize="sm"
                  placeholder="Ej. Juan Pérez"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setName(e.target.value)}
                />
              </InputGroup>
              <Text my={4} fontSize="sm" color="gray.500">
                Este será tu nombre visible para otros usuarios. Solo puede
                contener letras.
              </Text>
            </FormControl>
          </MotionBox>
        );
      case 1:
        return (
          <MotionBox
            key="username"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <FormControl>
              <FormLabel fontSize="md" fontWeight="bold">
                Nombre de usuario
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faUser} />
                </InputLeftElement>
                <InputRightElement pointerEvents="none">
                  {isLoadingUsername && <LoadingSpinner size="sm" />}
                </InputRightElement>
                <Input
                  type="text"
                  value={username}
                  fontSize="sm"
                  placeholder="Ej. juanperez123"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  isDisabled={isLoadingUsername}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </InputGroup>
              <Text my={4} fontSize="sm" color="gray.500">
                Este será tu nombre de usuario único. No podrás cambiarlo. Solo
                puede contener letras y números.
              </Text>
            </FormControl>
          </MotionBox>
        );
      case 2:
        return (
          <MotionBox
            key="email"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <FormControl>
              <FormLabel fontSize="md" fontWeight="bold">
                Correo electrónico
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faEnvelope} />
                </InputLeftElement>
                <InputRightElement pointerEvents="none">
                  {isLoadingEmail && <LoadingSpinner size="sm" />}
                </InputRightElement>
                <Input
                  type="email"
                  value={email}
                  fontSize="sm"
                  placeholder="Ej. juanperez@gmail.com"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  isDisabled={isLoadingEmail}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </InputGroup>
              <FormLabel mt={2} fontSize="md" fontWeight="bold">
                Número de celular
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faEnvelope} />
                </InputLeftElement>
                <InputRightElement pointerEvents="none">
                  {isLoadingEmail && <LoadingSpinner size="sm" />}
                </InputRightElement>
                <Input
                  type="phoneNumber"
                  value={phoneNumber}
                  fontSize="sm"
                  placeholder="Ej. 09XXXXXXXX"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  isDisabled={isLoadingEmail}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </InputGroup>
              <Text mt={2} fontSize="sm" color="gray.500">
                Servirán para enviarte notificaciones y recuperar tu cuenta.
              </Text>
            </FormControl>
          </MotionBox>
        );
      case 3:
        return (
          <MotionBox
            key="password"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <FormControl>
              <FormLabel fontSize="md" fontWeight="bold">
                Contraseña
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faLock} />
                </InputLeftElement>
                <InputRightElement>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </Button>
                </InputRightElement>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  fontSize="sm"
                  placeholder="Ingresa tu contraseña"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </InputGroup>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontSize="md" fontWeight="bold">
                Confirmar contraseña
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faLock} />
                </InputLeftElement>
                <InputRightElement>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setShowConfirmationPassword(!showConfirmationPassword)
                    }
                  >
                    <FontAwesomeIcon
                      icon={showConfirmationPassword ? faEyeSlash : faEye}
                    />
                  </Button>
                </InputRightElement>
                <Input
                  type={showConfirmationPassword ? "text" : "password"}
                  value={confirmPassword}
                  fontSize="sm"
                  placeholder="Confirma tu contraseña"
                  borderWidth="2px"
                  borderColor={error ? "red.300" : "whiteAlpha.400"}
                  onFocus={() => setError("")}
                  _focus={{ borderColor: "brand.400" }}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </InputGroup>
              {!error && (
                <Text my={4} fontSize="sm" color="gray.500">
                  Debe tener al menos 8 caracteres, incluyendo una mayúscula,
                  una minúscula, un número y un carácter especial.
                </Text>
              )}
            </FormControl>
          </MotionBox>
        );

      default:
        return null;
    }
  };

  return (
    <VStack flex="1" justifyContent="start" align="stretch" position="relative">
      {isAccountCreated ? (
        <MotionBox
          key="created"
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection="column"
          flex="0.5"
        >
          <motion.div
            animate={{ scale: [0, 1.4, 1], opacity: [0, 1] }}
            transition={{ duration: 0.5 }}
          >
            <FontAwesomeIcon
              icon={faCheckCircle}
              size="4x"
              style={{ color: "#48BB78", marginBottom: "16px" }}
            />
          </motion.div>

          <Text textAlign="center" fontSize="xl" fontWeight="bold" mb={2}>
            ¡Cuenta creada con éxito!
          </Text>

          <Text textAlign="center" fontSize="md" color="gray.600" mb={4}>
            ¡Gracias por unirte a nuestra comunidad! Aquí están los detalles de
            tu cuenta:
          </Text>

          <Box bg="whiteAlpha.200" p={5} borderRadius="2xl" mb={6} w="full">
            <VStack align="start" spacing={2}>
              <Box w="full" display="flex" justifyContent="space-between">
                <Text fontWeight="semibold">Nombre:</Text>
                <Text>{name}</Text>
              </Box>

              <Box w="full" display="flex" justifyContent="space-between">
                <Text fontWeight="semibold">Usuario:</Text>
                <Text>{username}</Text>
              </Box>

              <Box w="full" display="flex" justifyContent="space-between">
                <Text fontWeight="semibold">Correo:</Text>
                <Text>{email}</Text>
              </Box>
            </VStack>
          </Box>

          <Button
            onClick={handleShowLogin}
            bg="brand.500"
            color="brand.900"
            size="lg"
            borderRadius="2xl"
            w="100%"
            position="absolute"
            bottom={0}
            left={0}
          >
            Iniciar sesión
          </Button>
        </MotionBox>
      ) : (
        <>
          <Box position="absolute" top="-5" right="-5">
            <IconButton
              onClick={handleShowLogin}
              isDisabled={
                isCreatingAccount || isLoadingUsername || isLoadingEmail
              }
              icon={<CloseIcon />}
              aria-label="Cerrar"
              size="sm"
              bg={"brand.900"}
              color={"brand.800"}
            />
          </Box>

          <Heading
            fontFamily="Littera"
            fontWeight="bold"
            as="h1"
            size="md"
            textAlign="center"
            mb={4}
          >
            Crea tu cuenta Codex
          </Heading>

          <Stepper mb={4} size="md" colorScheme="green" index={activeStep}>
            {steps.map((step, index) => (
              <Step key={step}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={step}
                    active={step}
                  />
                </StepIndicator>
                {index !== steps.length - 1 && <StepSeparator />}
              </Step>
            ))}
          </Stepper>

          {isCreatingAccount ? (
            <MotionBox
              key="creating"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              flex="0.5"
            >
              <Text fontSize="xl" mb={4}>
                Creando cuenta...
              </Text>
              <LoadingSpinner size="xl" />
            </MotionBox>
          ) : (
            renderStep()
          )}

          {error && (
            <MotionAlert variant="glass"
              fontSize="sm"
              my={2}
              status="error"
              borderRadius={12}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 10,
                duration: 0.4,
              }}
            >
              <AlertIcon />
              {error}
              <CloseButton
                size="sm"
                position="absolute"
                right="2px"
                top="8px"
                onClick={() => setError("")}
              />
            </MotionAlert>
          )}

          <Box
            position="absolute"
            bottom="0"
            left="0"
            w="100%"
            display="flex"
            justifyContent="space-between"
            bg="glass.strong"
          >
            <Button
              onClick={handlePrevStep}
              isDisabled={
                activeStep === 0 ||
                isCreatingAccount ||
                isLoadingUsername ||
                isLoadingEmail
              }
              bg="brand.500"
              color="brand.900"
              size="lg"
              borderRadius="2xl"
            >
              Atrás
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button
                onClick={handleNextStep}
                isDisabled={
                  isLoadingUsername || isLoadingEmail || isCreatingAccount
                }
                bg="brand.500"
                color="brand.900"
                size="lg"
                borderRadius="2xl"
              >
                Siguiente
              </Button>
            ) : (
              <Button
                onClick={handleRegister}
                isDisabled={isCreatingAccount}
                bg="brand.500"
                color="brand.900"
                size="lg"
                borderRadius="2xl"
              >
                Crear cuenta
              </Button>
            )}
          </Box>
        </>
      )}
    </VStack>
  );
};

export default RegisterForm;
